// Shapes GET /api/applications_hub/history into chart series. Kept free of React so it can be
// tested on its own.

export const MEASURES = [
  { key: "started", label: "Started", color: "#94a3b8" },
  { key: "submitted", label: "Submitted", color: "#2b8cff" },
  { key: "accepted", label: "Accepted", color: "#22a559" },
  { key: "rejected", label: "Rejected", color: "#e5484d" },
  { key: "waitlisted", label: "Waitlisted", color: "#f59e0b" },
  { key: "declined", label: "Declined", color: "#a855f7" },
  { key: "withdrawn", label: "Withdrawn", color: "#64748b" },
] as const;
type FunnelKey = (typeof MEASURES)[number]["key"];

// Extra measures for the per-chapter and per-track charts; the funnel keeps the seven above.
// "average" is derived (rating_sum / ratings over the matching rows), never summed.
export const REVIEW_MEASURES = [
  { key: "ratings", label: "Ratings given", color: "#0ea5e9" },
  { key: "average", label: "Average rating", color: "#f97316" },
  { key: "manager_approved", label: "Manager approved", color: "#16a34a" },
  { key: "manager_rejected", label: "Manager declined", color: "#dc2626" },
  { key: "manager_pending", label: "Awaiting manager", color: "#eab308" },
] as const;
export const CHART_MEASURES = [...MEASURES, ...REVIEW_MEASURES];
export type MeasureKey = FunnelKey | (typeof REVIEW_MEASURES)[number]["key"];

export const TENURE = [
  { key: "tenure_under_1", label: "Under 1 year", color: "#93c5fd" },
  { key: "tenure_1_to_3", label: "1–3 years", color: "#2b8cff" },
  { key: "tenure_3_to_5", label: "3–5 years", color: "#8b5cf6" },
  { key: "tenure_5_to_10", label: "5–10 years", color: "#14b8a6" },
  { key: "tenure_10_plus", label: "10+ years", color: "#f59e0b" },
  { key: "tenure_unknown", label: "Not given", color: "#94a3b8" },
] as const;
type TenureKey = (typeof TENURE)[number]["key"];
type StoredKey = Exclude<MeasureKey, "average"> | TenureKey | "rating_sum" | "raters";

export const TRACKS = [
  { id: 1, label: "Mentees", color: "#2b8cff" },
  { id: 2, label: "Mentors", color: "#8b5cf6" },
  { id: 3, label: "Committee Members", color: "#14b8a6" },
] as const;

const CHAPTER_COLORS = ["#2b8cff", "#8b5cf6", "#14b8a6", "#f59e0b", "#e5484d", "#22a559", "#ec4899", "#64748b"];
const YEAR_COLORS = ["#cbd5e1", "#a5b4fc", "#93c5fd", "#5eead4", "#fcd34d", "#fca5a5", "#c4b5fd", "#86efac"];
export const CURRENT_YEAR_COLOR = "#e11d74";

export interface HistoryChapter {
  id: number;
  name: string;
}

export type CountRow = {
  year: number;
  chapter_id: number;
  applicant_type_id: number;
} & Record<StoredKey, number>;

export interface PaceRow {
  year: number;
  chapter_id: number;
  day: number;
  count: number;
}

export interface HistoryData {
  current_year: number;
  chapters: HistoryChapter[];
  counts: CountRow[];
  pace: PaceRow[];
}

// A point on a chart: the x value plus one numeric (or missing) value per series key.
export type Point = Record<string, number | null>;

export interface Series {
  key: string;
  label: string;
  color: string;
}

export const chapterColor = (index: number) => CHAPTER_COLORS[index % CHAPTER_COLORS.length];

export const yearsOf = (data: HistoryData) => [...new Set(data.counts.map((r) => r.year))].sort((a, b) => a - b);

const inChapter = (row: { chapter_id: number }, chapterId: number | null) =>
  chapterId === null || row.chapter_id === chapterId;
const inTrack = (row: CountRow, trackId: number | null) => trackId === null || row.applicant_type_id === trackId;

// Sums one measure over the matching rows; null when nothing matches, so the line has a gap
// there instead of a misleading zero (e.g. a chapter or track that didn't exist yet).
const sumOf = (rows: CountRow[], key: StoredKey) => rows.reduce((sum, r) => sum + (r[key] ?? 0), 0);
const total = (rows: CountRow[], measure: MeasureKey | TenureKey) => {
  if (!rows.length) return null;
  if (measure !== "average") return sumOf(rows, measure);
  const ratings = sumOf(rows, "ratings");
  return ratings ? Math.round((sumOf(rows, "rating_sum") / ratings) * 100) / 100 : null;
};

export const isDecimal = (measure: MeasureKey) => measure === "average";

// One line per tenure bucket (years at the company when they applied).
export function tenureSeries(data: HistoryData, chapterId: number | null, trackId: number | null): Point[] {
  return yearsOf(data).map((year) => {
    const rows = data.counts.filter((r) => r.year === year && inChapter(r, chapterId) && inTrack(r, trackId));
    const point: Point = { year };
    for (const t of TENURE) point[t.key] = total(rows, t.key);
    return point;
  });
}

// One line per chapter for a single measure.
export function chapterSeries(data: HistoryData, measure: MeasureKey, trackId: number | null): Point[] {
  return yearsOf(data).map((year) => {
    const point: Point = { year };
    for (const c of data.chapters) {
      const rows = data.counts.filter((r) => r.year === year && r.chapter_id === c.id && inTrack(r, trackId));
      point[String(c.id)] = total(rows, measure);
    }
    return point;
  });
}

// One line per measure (the enrollment funnel) for a chapter, or every chapter when chapterId is null.
export function funnelSeries(data: HistoryData, chapterId: number | null, trackId: number | null): Point[] {
  return yearsOf(data).map((year) => {
    const rows = data.counts.filter((r) => r.year === year && inChapter(r, chapterId) && inTrack(r, trackId));
    const point: Point = { year };
    for (const m of MEASURES) point[m.key] = total(rows, m.key);
    return point;
  });
}

// One line per track for a single measure.
export function trackSeries(data: HistoryData, chapterId: number | null, measure: MeasureKey): Point[] {
  return yearsOf(data).map((year) => {
    const point: Point = { year };
    for (const t of TRACKS) {
      const rows = data.counts.filter(
        (r) => r.year === year && inChapter(r, chapterId) && r.applicant_type_id === t.id
      );
      point[String(t.id)] = total(rows, measure);
    }
    return point;
  });
}

export const paceYears = (data: HistoryData) => [...new Set(data.pace.map((r) => r.year))].sort((a, b) => a - b);

export const yearColor = (year: number, currentYear: number, index: number) =>
  year === currentYear ? CURRENT_YEAR_COLOR : YEAR_COLORS[index % YEAR_COLORS.length];

// Cumulative submissions by day of the cohort year, one line per year. The current year stops at
// today so it doesn't run flat into the future.
export function paceSeries(data: HistoryData, chapterId: number | null, today: Date = new Date()): Point[] {
  const rows = data.pace.filter((r) => inChapter(r, chapterId));
  if (!rows.length) return [];
  const years = paceYears(data);
  const first = Math.min(...rows.map((r) => r.day));
  const last = Math.max(...rows.map((r) => r.day));
  const todayDay = Math.floor(
    (Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) - Date.UTC(data.current_year, 0, 1)) / 86400000
  );

  const perYear = new Map<number, Map<number, number>>();
  for (const r of rows) {
    const days = perYear.get(r.year) ?? new Map<number, number>();
    days.set(r.day, (days.get(r.day) ?? 0) + r.count);
    perYear.set(r.year, days);
  }

  const running = new Map<number, number>(years.map((y) => [y, 0]));
  const points: Point[] = [];
  for (let day = first; day <= last; day++) {
    const point: Point = { day };
    for (const y of years) {
      const value = (running.get(y) ?? 0) + (perYear.get(y)?.get(day) ?? 0);
      running.set(y, value);
      point[String(y)] = !perYear.has(y) || (y === data.current_year && day > todayDay) ? null : value;
    }
    points.push(point);
  }
  return points;
}

// "Jan 30" for day 29 of the cohort year. A non-leap reference year keeps every year aligned.
export const dayLabel = (day: number) =>
  new Date(Date.UTC(2001, 0, 1 + day)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

export const yearLabel = (year: number, currentYear: number) =>
  year === currentYear ? `${year} (so far)` : String(year);
